import React, {MouseEvent, PureComponent} from "react"
import styled from "styled-components"

import {ApolloState} from "@irati/utils/services/apollo/apolloStateInterface"

import buttonCss, {ButtonCssProps} from "./styled"
import Load, {LoadProps} from "../FormItem/utils/Load"
import Loader from "../Loading/Loader"
import Flex from "../Flex"
import Icon, {IconTypes} from "../Icon"

const DEFAULT_LOADING = <Loader />
const MIN_WIDTH_NEEDED_TO_DISPLAY_DEFAULT_LOADER = 39

interface ChildWrapperProps {
  isLoading?: boolean
  loading?: any
  isLargeEnoughForLoader?: boolean
}

const StyledButton = styled.button<ButtonCssProps>`
  ${buttonCss}
`

const ChildWrapper = styled.span<ChildWrapperProps>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  ${(props) => Button.getChildWrapperDisplayCss(props)}
`

export interface Props extends LoadProps, ButtonCssProps {
  id?: string
  className?: string
  onClick?: (e?: MouseEvent<HTMLButtonElement>) => any
  disabled?: boolean
  loading?: any
  apolloState?: ApolloState | any
  loadRef?: any
  icon?: IconTypes
  type?: "submit"
}

interface State {
  loaded?: boolean
}

class Button extends PureComponent<Props, State> {
  static defaultProps = {
    disabled: false,
    loading: DEFAULT_LOADING,
    apolloState: {}
  }

  static isUsingDefaultLoader = (loading: any) => {
    return loading === DEFAULT_LOADING
  }

  static getChildWrapperDisplayCss = ({isLoading, loading, isLargeEnoughForLoader}: ChildWrapperProps) => {
    if (loading === null) return
    if (Button.isUsingDefaultLoader(loading)) {
      if (!isLargeEnoughForLoader) return
      return `visibility: ${isLoading ? "hidden" : "inherit"};`
    }
    return `display: ${isLoading ? "none" : "inline-block"};`
  }

  buttonComponent = React.createRef<any>()

  loader?: Load
  state: State = {}

  componentDidMount() {
    this.loader = new Load(this)
  }

  componentWillUnmount() {
    if (this.loader) this.loader.destroy()
  }

  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    if (this.loader) this.loader.update({load: nextProps.load})
  }

  isButtonLargeEnoughForLoader = () => {
    if (this.buttonComponent.current) {
      const {width = 0} = this.buttonComponent.current.getBoundingClientRect()
      return width > MIN_WIDTH_NEEDED_TO_DISPLAY_DEFAULT_LOADER
    }
    return false
  }

  isThisButtonLoading = () => {
    const {apolloState, loadRef} = this.props
    const {loaded} = this.state

    if (loaded && !apolloState.loading) {
      return false
    }

    return !(loadRef && loadRef.current !== this)
  }

  render() {
    const {disabled, apolloState, loading, children, small, large, icon, ...props} = this.props
    const {loaded} = this.state

    const isThisButtonLoading = this.isThisButtonLoading()
    const isLoading = isThisButtonLoading && (!loaded || apolloState.loading)
    const isDisabled = disabled || (!isThisButtonLoading && !loaded)

    const isLargeEnoughForLoader = this.isButtonLargeEnoughForLoader()
    const isUsingDefaultLoader = Button.isUsingDefaultLoader(loading)
    const isAllowedToDisplayLoading = (isUsingDefaultLoader && isLargeEnoughForLoader) || !isUsingDefaultLoader

    return (
      <StyledButton
        {...props}
        type={props.type ?? "button"}
        small={small}
        large={large}
        ref={this.buttonComponent}
        disabled={isLoading || isDisabled}>
        <Flex alignItems="center">
          {isLoading && isAllowedToDisplayLoading && loading}
          <ChildWrapper isLoading={isLoading} loading={loading} isLargeEnoughForLoader={isLargeEnoughForLoader}>
            {icon && <Icon icon={icon} />}
            {children}
          </ChildWrapper>
        </Flex>
      </StyledButton>
    )
  }
}

export default Button
